import React from "react";
import { vision } from "../../data/vision";
import Reveal from "../Reveal";
import FlowingRibbon from "./FlowingRibbon";
import TextileRibbon from "./TextileRibbon";
import { ArrowRight, ArrowDown } from "lucide-react";

/**
 * Vision — the "Our Vision" section.
 * Editorial, scroll-led experience made up of 7 subsections that share a
 * flowing ribbon motif. Warm-white background, deep purple + refined green
 * accents, generous whitespace.
 */
const Vision = () => {
  return (
    <section
      id="vision"
      className="relative scroll-mt-24 md:scroll-mt-32 bg-[#FBF8F2] text-neutral-900"
      aria-label="Our Vision"
    >
      {/* SECTION 1 — Opening */}
      <div className="relative min-h-[86vh] flex items-start overflow-hidden">
        {/* Official NYLE vision textile ribbon — anchored slightly lower, static */}
        <div
          className="absolute right-[-2%] bottom-[-6%] w-[86%] md:w-[80%] pointer-events-none"
          aria-hidden="true"
        >
          <img
            src="https://customer-assets-gfyr7b9c.emergentagent.net/job_web-launch-512/artifacts/q5f1b9hi_NYLE-Vision-Textile-Ribbon-Transparent.png"
            alt=""
            className="w-full h-auto block select-none"
            style={{
              filter:
                "drop-shadow(0 30px 40px rgba(80, 30, 120, 0.16)) drop-shadow(0 8px 16px rgba(80, 30, 120, 0.08))",
            }}
          />
        </div>

        <div className="relative z-10 w-full max-w-[1400px] mx-auto px-6 md:px-12 pt-14 md:pt-16 pb-16">
          <div className="max-w-2xl lg:max-w-3xl">
            <Reveal>
              <p
                className="text-[#6FA84A] uppercase font-['Montserrat'] font-medium text-xs md:text-sm"
                style={{ letterSpacing: "0.16em" }}
              >
                {vision.eyebrow}
              </p>
            </Reveal>
            <Reveal delay={120}>
              <h2
                className="mt-8 font-['Cormorant_Garamond'] text-neutral-900 leading-[1.05] text-5xl sm:text-6xl md:text-7xl lg:text-8xl"
                style={{ fontWeight: 500, letterSpacing: "-0.005em" }}
              >
                Building the
                <br />
                brands of tomorrow.
              </h2>
            </Reveal>
            <Reveal delay={240}>
              <p className="mt-8 max-w-md font-['Montserrat'] font-normal text-neutral-500 text-base md:text-lg leading-relaxed">
                Where ideas, talent and technology
                <br className="hidden sm:block" />
                come into flow.
              </p>
            </Reveal>
          </div>
        </div>

        {/* Scroll indicator */}
        <a
          href="#vision-stages"
          onClick={(e) => {
            e.preventDefault();
            window.scrollBy({ top: window.innerHeight * 0.9, behavior: "smooth" });
          }}
          className="absolute bottom-4 md:bottom-6 left-1/2 -translate-x-1/2 z-20 group flex flex-col items-center gap-3"
          aria-label="Scroll to explore"
        >
          <span
            className="font-['Montserrat'] font-medium text-[#7B3FA7] text-[10px] md:text-[11px] uppercase"
            style={{ letterSpacing: "0.12em" }}
          >
            Scroll to explore
          </span>
          <span className="flex items-center justify-center w-11 h-11 rounded-full border border-[#7B3FA7]/50 bg-white/70 backdrop-blur-sm text-[#7B3FA7] group-hover:border-[#7B3FA7] group-hover:bg-white transition-colors">
            <ArrowDown
              size={18}
              className="animate-[floatY_2.4s_ease-in-out_infinite]"
            />
          </span>
        </a>
      </div>

      {/* SECTION 3 — Progressive vision reveal */}
      <div id="vision-statement" className="relative py-28 md:py-40 border-t border-neutral-900/5">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12 text-center">
          <Reveal>
            <p className="font-['Montserrat'] font-medium uppercase text-[#6FA84A] text-xs md:text-sm" style={{ letterSpacing: "0.18em" }}>
              OUR VISION
            </p>
          </Reveal>

          <div className="mt-14 space-y-6 md:space-y-8">
            <Reveal delay={80}>
              <p className="font-serif italic text-neutral-500 text-2xl md:text-3xl leading-snug">
                To build an{' '}
                <span className="not-italic font-semibold text-[#7B3FA7]">
                  enduring
                </span>{' '}
                global apparel enterprise
              </p>
            </Reveal>
            <Reveal delay={220}>
              <p className="font-serif italic text-neutral-500 text-2xl md:text-3xl leading-snug">
                where ideas, talent and technology
              </p>
            </Reveal>
            <Reveal delay={360}>
              <p className="font-serif italic text-2xl md:text-3xl leading-snug">
                <span className="not-italic font-semibold text-[#6FA84A]">
                  flow together
                </span>
              </p>
            </Reveal>
            <Reveal delay={500}>
              <p className="font-serif italic text-neutral-500 text-2xl md:text-3xl leading-snug">
                to enable the{' '}
                <span className="not-italic font-semibold text-[#7B3FA7]">
                  brands of tomorrow.
                </span>
              </p>
            </Reveal>
          </div>
        </div>

        {/* Scroll indicator — Vision Statement → next section */}
        <a
          href="#vision-narrative"
          onClick={(e) => {
            e.preventDefault();
            window.scrollBy({ top: window.innerHeight * 0.85, behavior: "smooth" });
          }}
          className="absolute bottom-6 md:bottom-8 left-1/2 -translate-x-1/2 z-20 group flex flex-col items-center gap-3"
          aria-label="Scroll to next section"
        >
          <span
            className="font-['Montserrat'] font-medium text-[#7B3FA7] text-[10px] md:text-[11px] uppercase"
            style={{ letterSpacing: "0.12em" }}
          >
            Scroll to explore
          </span>
          <span className="flex items-center justify-center w-11 h-11 rounded-full border border-[#7B3FA7]/50 bg-white/70 backdrop-blur-sm text-[#7B3FA7] group-hover:border-[#7B3FA7] group-hover:bg-white transition-colors">
            <ArrowDown
              size={18}
              className="animate-[floatY_2.4s_ease-in-out_infinite]"
            />
          </span>
        </a>
      </div>

      {/* SECTION 5 — Supporting narrative */}
      <div id="vision-narrative" className="relative pt-28 md:pt-36 pb-32 md:pb-40 border-t border-neutral-900/5">
        <div className="max-w-[1100px] mx-auto px-6 md:px-12">
          <Reveal>
            <p
              className="font-['Montserrat'] font-medium uppercase text-[#5AA84A] text-xs md:text-sm"
              style={{ letterSpacing: "0.18em" }}
            >
              THE NYLE PROPOSITION
            </p>
          </Reveal>
          <Reveal delay={140}>
            <blockquote className="mt-12 md:mt-14 font-extrabold tracking-[-0.02em] leading-[1.8] text-neutral-900 text-4xl sm:text-5xl md:text-6xl">
              “We see apparel not as an endpoint, but as the foundation of an{' '}
              <span className="text-[#7B3FA7]">enduring enterprise</span>.”
            </blockquote>
          </Reveal>
          <Reveal delay={280}>
            <p className="mt-16 md:mt-20 max-w-3xl font-['Montserrat'] font-normal text-neutral-500 text-base md:text-lg leading-[1.95]">
              {vision.narrativeBody}
            </p>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default Vision;
